import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, useGLTF, Environment, Line, Text, TransformControls } from '@react-three/drei';
import * as THREE from 'three';
import QRCode from 'react-qr-code';

// =========================================================================
// Pose Sync — receives camera, hand, and gaze data from room WebSocket
// Supports switchable quaternion conversion modes for debugging alignment
// =========================================================================
function PoseSync({ wsUrl, followPresenter, onConnectionChange, onPoseUpdate, onModelSwap, onModelOffset, onInteractiveState, onPropEvent, onModelScale, onLaserPointer, onDrawingStroke, onParticipantsChange, initialPresenter }) {
  const { camera } = useThree();
  const targetPos = useRef(new THREE.Vector3());
  const targetQuat = useRef(new THREE.Quaternion());
  const wsRef = useRef(null);
  const hasTarget = useRef(false);
  const presenterIdRef = useRef(null); // lock to first pose sender
  const allPosesRef = useRef({}); // { humuloUserId: { cam, hands, gaze, displayName, ts } }
  const participantsRef = useRef(new Set()); // track joined VR users

  useEffect(() => {
    if (!wsUrl) return;
    // Use server-authoritative presenter if available, otherwise discover from first pose
    presenterIdRef.current = initialPresenter || null;
    if (initialPresenter) {
      console.log(`[PoseSync] Presenter set from server: ${initialPresenter}`);
    }

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[PoseSync] WebSocket connected');
      window._workshopWs = ws; // Expose for presenter components to send messages
      onConnectionChange?.('connected');
    };
    ws.onclose = () => {
      console.log('[PoseSync] WebSocket disconnected');
      window._workshopWs = null;
      onConnectionChange?.('disconnected');
    };
    ws.onerror = (e) => {
      console.error('[PoseSync] WebSocket error:', e);
      onConnectionChange?.('error');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        window._wsCount = (window._wsCount || 0) + 1;
        if (window._wsCount <= 3 || window._wsCount % 100 === 0) {
          console.log(`[WS] msg #${window._wsCount} type=${data.type}`);
        }
        
        if (data.type === 'pose') {
          // Server injects humuloUserId on pose relay
          const senderId = data.humuloUserId || data.connectionId;
          if (!presenterIdRef.current && senderId) {
            presenterIdRef.current = senderId;
            console.log(`[PoseSync] Locked to presenter: ${senderId}`);
          }

          // Store pose for ALL VR participants (WS-018)
          if (senderId) {
            allPosesRef.current[senderId] = {
              cam: data.cam,
              hands: data.hands,
              gaze: data.gaze,
              displayName: data.displayName || senderId,
              ts: Date.now(),
            };
            // Notify parent of all participant poses (throttled to every 3rd frame)
            if (!window._participantPoseCounter) window._participantPoseCounter = 0;
            window._participantPoseCounter++;
            if (window._participantPoseCounter % 3 === 0) {
              onParticipantsChange?.({ ...allPosesRef.current });
            }
          }

          // Camera lock: only follow the presenter
          const isPresenter = presenterIdRef.current && senderId === presenterIdRef.current;
          if (isPresenter) {
            const cam = data.cam;
            if (cam) {
              // Position: Unity left-handed → Three.js right-handed = negate Z
              targetPos.current.set(cam.p[0], cam.p[1], -cam.p[2]);
              
              // Quaternion: standard Unity→Three.js handedness flip = negate X and Y
              const [qx, qy, qz, qw] = cam.r;
              targetQuat.current.set(-qx, -qy, qz, qw);
              
              hasTarget.current = true;
              onConnectionChange?.('live');
              
              // Update diagnostic state (every 300ms)
              if (!window._lastDiag || Date.now() - window._lastDiag > 300) {
                window._lastDiag = Date.now();
                const euler = new THREE.Euler().setFromQuaternion(targetQuat.current, 'YXZ');
                const deg = (r) => (r * 180 / Math.PI).toFixed(1);
                window._diagData = {
                  unity_pos: cam.p.map(v => v.toFixed(2)),
                  unity_quat: cam.r.map(v => v.toFixed(3)),
                  three_pos: [cam.p[0].toFixed(2), cam.p[1].toFixed(2), (-cam.p[2]).toFixed(2)],
                  yaw: deg(euler.y),
                  pitch: deg(euler.x),
                  roll: deg(euler.z),
                  mode: 'standard',
                  seq: data.seq,
                };
              }
            }
            onPoseUpdate?.(data);
          }
        }
        
        if (data.type === 'model_swap') {
          console.log(`[PoseSync] model_swap received: ${data.modelUrl} (${data.modelName})`);
          onModelSwap?.(data.modelUrl, data.modelName);
        }

        if (data.type === 'model_offset' && data.pos) {
          // Presenter moved/rotated model — delta from spawn
          // SAME conversion as camera pose (both are EnvironmentMain-local):
          // Position: negate Z for handedness (Unity LH → Three.js RH)
          // Quaternion: negate X and Y (standard Unity→Three.js handedness flip)
          const r = data.rot || [0, 0, 0, 1];
          onModelOffset?.(
            data.pos[0], data.pos[1], -data.pos[2],
            -r[0], -r[1], r[2], r[3]
          );
        }

        if (data.type === 'presenter_changed' && data.presenterUserId) {
          console.log(`[PoseSync] Presenter changed to: ${data.presenterUserId}`);
          presenterIdRef.current = data.presenterUserId;
        }

        // WS-018: Track VR participant join/leave for avatar rendering
        if (data.type === 'join' && data.humuloUserId) {
          participantsRef.current.add(data.humuloUserId);
          console.log(`[PoseSync] VR user joined: ${data.humuloUserId} (${data.displayName})`);
        }
        if (data.type === 'leave' && data.humuloUserId) {
          participantsRef.current.delete(data.humuloUserId);
          delete allPosesRef.current[data.humuloUserId];
          onParticipantsChange?.({ ...allPosesRef.current });
          console.log(`[PoseSync] VR user left: ${data.humuloUserId}`);
        }

        // WS-017: Model scale from presenter
        if (data.type === 'model_scale' && data.scale != null) {
          console.log(`[PoseSync] model_scale: ${data.scale}`);
          onModelScale?.(data.scale);
        }

        // WS-019: Laser pointer from presenter
        if (data.type === 'laser_pointer') {
          onLaserPointer?.(data);
        }

        // WS-021: Drawing strokes from presenter
        if (data.type === 'drawing_stroke') {
          onDrawingStroke?.(data);
        }

        if (data.type === 'interactive_state') {
          onInteractiveState?.(data);
        }

        // Grabbable prop events — spawn, move, despawn
        if (data.type === 'prop_spawn' || data.type === 'object_transform' || data.type === 'prop_despawn') {
          onPropEvent?.(data);
        }
      } catch (err) {
        // Ignore non-parseable messages
      }
    };

    return () => { window._workshopWs = null; ws.close(); };
  }, [wsUrl, onConnectionChange, onPoseUpdate, onModelSwap]);

  // Smooth interpolation toward presenter camera
  useFrame((_, delta) => {
    if (!followPresenter || !hasTarget.current) return;
    const t = 1 - Math.pow(0.001, delta);
    camera.position.lerp(targetPos.current, t);
    camera.quaternion.slerp(targetQuat.current, t);
  });

  return null;
}


// =========================================================================
// Presenter Avatar — head + hands with full pose (position + rotation)
// Shows the VR presenter's head and hands to Teams/browser viewers.
// Head visible only when viewer is orbiting freely (not following presenter).
// Hands always visible so viewers see the presenter interacting with models.
// =========================================================================
function PresenterAvatar({ latestPose, followPresenter }) {
  const headRef = useRef();
  const leftRef = useRef();
  const rightRef = useRef();
  
  // Smooth interpolation targets
  const headTargetPos = useRef(new THREE.Vector3());
  const headTargetQuat = useRef(new THREE.Quaternion());
  const leftTargetPos = useRef(new THREE.Vector3());
  const leftTargetQuat = useRef(new THREE.Quaternion());
  const rightTargetPos = useRef(new THREE.Vector3());
  const rightTargetQuat = useRef(new THREE.Quaternion());
  
  useFrame((_, delta) => {
    const t = 1 - Math.pow(0.001, delta); // same smoothing as camera
    
    // Head — from cam pose data
    if (latestPose?.cam && headRef.current) {
      const c = latestPose.cam;
      headTargetPos.current.set(c.p[0], c.p[1], -c.p[2]);
      headTargetQuat.current.set(-c.r[0], -c.r[1], c.r[2], c.r[3]);
      headRef.current.position.lerp(headTargetPos.current, t);
      headRef.current.quaternion.slerp(headTargetQuat.current, t);
      // Hide head when camera IS the presenter's viewpoint
      headRef.current.visible = !followPresenter;
    }
    
    // Hands — from hands pose data
    if (latestPose?.hands) {
      const h = latestPose.hands;
      const lp = Array.isArray(h.l) ? h.l : h.l?.p;
      const lr = h.l?.r;
      const rp = Array.isArray(h.r) ? h.r : h.r?.p;
      const rr = h.r?.r;
      
      if (lp && leftRef.current) {
        leftTargetPos.current.set(lp[0], lp[1], -lp[2]);
        leftRef.current.position.lerp(leftTargetPos.current, t);
        if (lr) {
          leftTargetQuat.current.set(-lr[0], -lr[1], lr[2], lr[3]);
          leftRef.current.quaternion.slerp(leftTargetQuat.current, t);
        }
        leftRef.current.visible = true;
      } else if (leftRef.current) {
        leftRef.current.visible = false;
      }
      
      if (rp && rightRef.current) {
        rightTargetPos.current.set(rp[0], rp[1], -rp[2]);
        rightRef.current.position.lerp(rightTargetPos.current, t);
        if (rr) {
          rightTargetQuat.current.set(-rr[0], -rr[1], rr[2], rr[3]);
          rightRef.current.quaternion.slerp(rightTargetQuat.current, t);
        }
        rightRef.current.visible = true;
      } else if (rightRef.current) {
        rightRef.current.visible = false;
      }
    }
  });

  // Hand mesh — palm ellipsoid + index finger pointer
  const HandMesh = ({ color }) => (
    <group>
      {/* Palm — slightly flattened sphere */}
      <mesh scale={[1, 0.6, 1.3]}>
        <sphereGeometry args={[0.04, 12, 8]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.3} transparent opacity={0.85} />
      </mesh>
      {/* Index finger pointer */}
      <mesh position={[0, 0, -0.06]} rotation={[Math.PI/2, 0, 0]}>
        <cylinderGeometry args={[0.008, 0.005, 0.05, 6]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.3} />
      </mesh>
      {/* Thumb stub */}
      <mesh position={[0.03, 0, -0.01]} rotation={[Math.PI/2, 0, Math.PI/6]}>
        <cylinderGeometry args={[0.007, 0.006, 0.03, 6]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.2} />
      </mesh>
    </group>
  );

  return (
    <>
      {/* Presenter head — visor-style helmet */}
      <group ref={headRef} visible={false}>
        {/* WS-022: Math.PI Y rotation so visor faces forward relative to Unity camera quat */}
        <group rotation={[0, Math.PI, 0]}>
          {/* Head sphere */}
          <mesh>
            <sphereGeometry args={[0.1, 16, 12]} />
            <meshStandardMaterial color="#475569" transparent opacity={0.6} />
          </mesh>
          {/* Visor (face plate) — darker front */}
          <mesh position={[0, 0.01, -0.06]}>
            <sphereGeometry args={[0.07, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color="#0ea5e9" emissive="#0ea5e9" emissiveIntensity={0.4} transparent opacity={0.7} />
          </mesh>
        </group>
      </group>
      
      {/* Left hand — blue */}
      <group ref={leftRef} visible={false}>
        <HandMesh color="#3b82f6" />
      </group>
      
      {/* Right hand — red */}
      <group ref={rightRef} visible={false}>
        <HandMesh color="#ef4444" />
      </group>
    </>
  );
}


// =========================================================================
// Remote VR Participant Avatars — WS-018
// Renders head + hands for ALL VR users visible in the 3JS viewer.
// WS-020: Head rotated 180° on Y to fix backwards orientation.
// WS-020: Left hand mirrored on X to fix inversion.
// =========================================================================
function RemoteAvatars({ participantPoses, presenterUserId, followPresenter }) {
  return (
    <>
      {Object.entries(participantPoses).map(([userId, poseData]) => (
        <RemoteParticipant key={userId} userId={userId} poseData={poseData} isPresenter={userId === presenterUserId} hideHead={userId === presenterUserId && followPresenter} />
      ))}
    </>
  );
}

// WS-018: GLB hand model URLs on S3
const LEFT_HAND_URL = 'https://humulo-models.s3.us-east-1.amazonaws.com/tenants/humulo-library/left_hand.glb';
const RIGHT_HAND_URL = 'https://humulo-models.s3.us-east-1.amazonaws.com/tenants/humulo-library/right_hand.glb';

// Preload hand models so first render isn't blank
useGLTF.preload(LEFT_HAND_URL);
useGLTF.preload(RIGHT_HAND_URL);

function HandGLB({ url, color }) {
  const gltf = useGLTF(url);
  const scene = React.useMemo(() => {
    const clone = gltf.scene.clone(true);
    clone.traverse(child => {
      if (child.isMesh) {
        child.material = child.material.clone();
        child.material.color.set(color);
        child.material.emissive = new THREE.Color(color);
        child.material.emissiveIntensity = 0.15;
        child.material.transparent = true;
        child.material.opacity = 0.9;
      }
    });
    return clone;
  }, [gltf.scene, color]);
  // XR Hands FBX models are tiny — scale up to match hand size
  return <primitive object={scene} scale={[1, 1, 1]} />;
}

function RemoteParticipant({ userId, poseData, isPresenter, hideHead }) {
  const headRef = useRef();
  const leftRef = useRef();
  const rightRef = useRef();
  const labelRef = useRef();
  const { camera } = useThree();
  const headTargetPos = useRef(new THREE.Vector3());
  const headTargetQuat = useRef(new THREE.Quaternion());
  const leftTargetPos = useRef(new THREE.Vector3());
  const leftTargetQuat = useRef(new THREE.Quaternion());
  const rightTargetPos = useRef(new THREE.Vector3());
  const rightTargetQuat = useRef(new THREE.Quaternion());

  useFrame((_, delta) => {
    const t = 1 - Math.pow(0.001, delta);
    if (poseData?.cam && headRef.current) {
      const c = poseData.cam;
      headTargetPos.current.set(c.p[0], c.p[1], -c.p[2]);
      headTargetQuat.current.set(-c.r[0], -c.r[1], c.r[2], c.r[3]);
      headRef.current.position.lerp(headTargetPos.current, t);
      headRef.current.quaternion.slerp(headTargetQuat.current, t);
      headRef.current.visible = !hideHead;
    }
    if (poseData?.hands) {
      const h = poseData.hands;
      const lp = Array.isArray(h.l) ? h.l : h.l?.p;
      const lr = h.l?.r;
      const rp = Array.isArray(h.r) ? h.r : h.r?.p;
      const rr = h.r?.r;
      if (lp && leftRef.current) {
        leftTargetPos.current.set(lp[0], lp[1], -lp[2]);
        leftRef.current.position.lerp(leftTargetPos.current, t);
        if (lr) { leftTargetQuat.current.set(-lr[0], -lr[1], lr[2], lr[3]); leftRef.current.quaternion.slerp(leftTargetQuat.current, t); }
        leftRef.current.visible = true;
      } else if (leftRef.current) { leftRef.current.visible = false; }
      if (rp && rightRef.current) {
        rightTargetPos.current.set(rp[0], rp[1], -rp[2]);
        rightRef.current.position.lerp(rightTargetPos.current, t);
        if (rr) { rightTargetQuat.current.set(-rr[0], -rr[1], rr[2], rr[3]); rightRef.current.quaternion.slerp(rightTargetQuat.current, t); }
        rightRef.current.visible = true;
      } else if (rightRef.current) { rightRef.current.visible = false; }
    }
    if (labelRef.current) labelRef.current.lookAt(camera.position);
  });

  const headColor = isPresenter ? '#6264A7' : '#475569';
  const visorColor = isPresenter ? '#0ea5e9' : '#22c55e';
  const lhColor = isPresenter ? '#3b82f6' : '#06b6d4';
  const rhColor = isPresenter ? '#ef4444' : '#f97316';

  return (
    <>
      <group ref={headRef} visible={false}>
        {/* WS-022: Re-added Math.PI Y rotation — visor must face forward relative to Unity camera quat */}
        <group rotation={[0, Math.PI, 0]}>
          <mesh><sphereGeometry args={[0.1, 16, 12]} /><meshStandardMaterial color={headColor} transparent opacity={0.6} /></mesh>
          <mesh position={[0, 0.01, -0.06]}><sphereGeometry args={[0.07, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2]} /><meshStandardMaterial color={visorColor} emissive={visorColor} emissiveIntensity={0.4} transparent opacity={0.7} /></mesh>
        </group>
        <group ref={labelRef} position={[0, 0.18, 0]}>
          <Text fontSize={0.05} color={isPresenter ? '#6264A7' : '#22c55e'} anchorX="center" anchorY="bottom" outlineWidth={0.003} outlineColor="#000000">
            {poseData?.displayName || userId?.split('_')[0] || 'VR User'}
          </Text>
        </group>
      </group>
      <group ref={leftRef} visible={false}><HandGLB url={LEFT_HAND_URL} color={lhColor} /></group>
      <group ref={rightRef} visible={false}><HandGLB url={RIGHT_HAND_URL} color={rhColor} /></group>
    </>
  );
}

// =========================================================================
// Laser Pointer — WS-019
// =========================================================================
function LaserPointerRenderer({ laserData }) {
  const lineRef = useRef();
  const dotRef = useRef();
  const points = useMemo(() => [new THREE.Vector3(), new THREE.Vector3()], []);
  useFrame(() => {
    if (!laserData?.active || !laserData?.origin || !lineRef.current) {
      if (lineRef.current) lineRef.current.visible = false;
      if (dotRef.current) dotRef.current.visible = false;
      return;
    }
    const o = laserData.origin;
    const d = laserData.direction;
    const len = laserData.hitDistance || 5.0;
    points[0].set(o[0], o[1], -o[2]);
    points[1].set(o[0] + d[0] * len, o[1] + d[1] * len, -(o[2] + d[2] * len));
    lineRef.current.geometry.setFromPoints(points);
    lineRef.current.visible = true;
    if (dotRef.current) { dotRef.current.position.copy(points[1]); dotRef.current.visible = true; }
  });
  return (
    <>
      <line ref={lineRef} visible={false}><bufferGeometry /><lineBasicMaterial color="#ff0000" transparent opacity={0.8} linewidth={2} /></line>
      <mesh ref={dotRef} visible={false}><sphereGeometry args={[0.015, 8, 8]} /><meshBasicMaterial color="#ff0000" /></mesh>
    </>
  );
}

// =========================================================================
// Drawing Strokes — WS-021
// =========================================================================
function DrawingRenderer({ strokes, modelYRot, modelOffset, modelRotDelta, modelScale }) {
  // WS-021 FIX: Drawing points from Unity are in world space. The model in 3JS has
  // π Y-rotation + offset + scale via its group wrapper. Drawing must be wrapped in
  // the SAME transform so Z-flipped points (Unity→GLB local space) land on the model
  // surface, not mirrored in empty space.
  const transform = useMemo(() => {
    const off = modelOffset || [0, 0, 0];
    const rd = modelRotDelta || [0, 0, 0, 1];
    const baseQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, modelYRot || 0, 0));
    const deltaQuat = new THREE.Quaternion(rd[0], rd[1], rd[2], rd[3]);
    const finalQuat = deltaQuat.clone().multiply(baseQuat);
    const finalEuler = new THREE.Euler().setFromQuaternion(finalQuat);
    const s = modelScale || 1;
    return { position: [off[0], off[1], off[2]], rotation: finalEuler, scale: [s, s, s] };
  }, [modelYRot, modelOffset, modelRotDelta, modelScale]);

  if (!strokes || strokes.length === 0) return null;
  return (
    <group position={transform.position} rotation={transform.rotation} scale={transform.scale}>
      {strokes.map((stroke, i) => (<DrawingStrokeLine key={stroke.id || i} points={stroke.points} color={stroke.color || '#ff0000'} />))}
    </group>
  );
}
function DrawingStrokeLine({ points, color }) {
  const threePoints = useMemo(() => {
    if (!points || points.length < 2) return null;
    // World coords from Unity with Z-flip for Three.js
    return points.map(p => [p[0], p[1], -p[2]]);
  }, [points]);
  if (!threePoints) return null;
  return (<Line points={threePoints} color={color} lineWidth={4} depthTest={false} />);
}


// =========================================================================
// Prop Renderer — renders grabbable props spawned by presenter
// Loads GLB mesh from S3, positions via object_transform messages
// =========================================================================
function PropRenderer({ props }) {
  return (
    <>
      {Object.entries(props).map(([propId, propData]) => (
        <PropMesh key={propId} propId={propId} {...propData} />
      ))}
    </>
  );
}

function PropMesh({ propId, modelUrl, position, rotation, held }) {
  const groupRef = useRef();
  const targetPos = useRef(new THREE.Vector3(...(position || [0, 1, 0])));
  const targetQuat = useRef(new THREE.Quaternion(...(rotation || [0, 0, 0, 1])));
  
  // Load GLB
  let gltf = null;
  let scene = null;
  try {
    gltf = useGLTF(modelUrl);
    scene = useMemo(() => gltf.scene.clone(), [gltf.scene]);
  } catch (e) {
    // Model still loading or failed
  }
  
  // Update targets when props state changes
  useEffect(() => {
    if (position) targetPos.current.set(position[0], position[1], -position[2]);
    if (rotation) targetQuat.current.set(-rotation[0], -rotation[1], rotation[2], rotation[3]);
  }, [position, rotation]);
  
  // Smooth interpolation
  useFrame((_, delta) => {
    if (!groupRef.current) return;
    const t = 1 - Math.pow(0.001, delta);
    groupRef.current.position.lerp(targetPos.current, t);
    groupRef.current.quaternion.slerp(targetQuat.current, t);
  });

  if (!scene) return null;
  
  return (
    <group ref={groupRef}>
      <primitive object={scene} />
      {/* Held indicator — subtle glow when presenter is grabbing */}
      {held && (
        <mesh>
          <boxGeometry args={[0.32, 0.22, 0.22]} />
          <meshStandardMaterial color="#fbbf24" transparent opacity={0.15} emissive="#fbbf24" emissiveIntensity={0.3} />
        </mesh>
      )}
    </group>
  );
}

// =========================================================================
// Gaze Ray — thin line from head in gaze direction
// =========================================================================
function GazeRay({ latestPose }) {
  const lineRef = useRef();
  const points = useMemo(() => [new THREE.Vector3(), new THREE.Vector3()], []);
  
  useFrame(() => {
    if (!latestPose?.gaze || !lineRef.current) return;
    const g = latestPose.gaze;
    const ox = g.o[0], oy = g.o[1], oz = -g.o[2];
    const dx = g.d[0], dy = g.d[1], dz = -g.d[2];
    const rayLength = 5.0;
    points[0].set(ox, oy, oz);
    points[1].set(ox + dx * rayLength, oy + dy * rayLength, oz + dz * rayLength);
    lineRef.current.geometry.setFromPoints(points);
    lineRef.current.visible = true;
  });
  
  return (
    <line ref={lineRef} visible={false}>
      <bufferGeometry />
      <lineBasicMaterial color="#fbbf24" transparent opacity={0.6} linewidth={1} />
    </line>
  );
}

// =========================================================================
// GLB Model in rotatable group
// =========================================================================
function Model({ url, yRotation, offset, rotDelta, modelScale, outerRef }) {
  const gltf = useGLTF(url);
  // Clone scene so each model swap gets a fresh Three.js object tree.
  // Without clone(), switching URLs reuses the cached object and goes blank.
  const scene = React.useMemo(() => gltf.scene.clone(true), [gltf.scene, url]);

  // Enable shadow casting on all meshes for realistic rendering
  useEffect(() => {
    if (!scene) return;
    scene.traverse(child => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }, [scene]);
  const innerRef = useRef();
  const mixerRef = useRef(null);
  const actionsRef = useRef({});
  const sprayRef = useRef(null);
  const sprayActiveRef = useRef(false);

  // Set up AnimationMixer — auto-play mechanical animations, keep interactive ones manual
  useEffect(() => {
    if (!gltf.animations || gltf.animations.length === 0) return;
    
    const mixer = new THREE.AnimationMixer(scene);
    mixerRef.current = mixer;
    const actions = {};

    // Known interactive clip names that should stay trigger-based (LoopOnce)
    const interactiveClipNames = ['PinPull', 'HandleSqueeze', 'ValveOpen'];
    const isInteractiveModel = gltf.animations.some(clip =>
      interactiveClipNames.some(name => clip.name.includes(name))
    );

    gltf.animations.forEach(clip => {
      const action = mixer.clipAction(clip);
      if (isInteractiveModel) {
        // Interactive model — manual trigger, play once
        action.setLoop(THREE.LoopOnce);
        action.clampWhenFinished = true;
      } else {
        // Mechanical/ambient model — auto-play, loop forever
        action.setLoop(THREE.LoopRepeat);
        action.clampWhenFinished = false;
      }
      actions[clip.name] = action;
      console.log(`[Model] Animation found: "${clip.name}" (${clip.duration.toFixed(2)}s) [${isInteractiveModel ? 'interactive' : 'auto-play'}]`);
    });
    actionsRef.current = actions;

    // Auto-play non-interactive animations immediately
    if (!isInteractiveModel) {
      Object.values(actions).forEach(action => action.play());
      console.log(`[Model] Auto-playing ${Object.keys(actions).length} mechanical animations`);
    }

    // Expose actions globally for console/UI triggering
    window._modelAnimations = actions;
    window._modelMixer = mixer;

    return () => {
      mixer.stopAllAction();
      mixerRef.current = null;
    };
  }, [scene, url, gltf.animations]);

  // Create spray particle system at Nozzle node
  useEffect(() => {
    if (!scene) return;
    
    let nozzleNode = null;
    scene.traverse(child => {
      if (child.name === 'Nozzle') nozzleNode = child;
    });
    
    if (!nozzleNode) return;
    console.log('[Model] Found Nozzle node — creating spray particles');

    const particleCount = 200;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const velocities = new Float32Array(particleCount * 3);
    const lifetimes = new Float32Array(particleCount);

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = 0;
      positions[i * 3 + 1] = 0;
      positions[i * 3 + 2] = 0;
      velocities[i * 3] = (Math.random() - 0.5) * 0.3;
      velocities[i * 3 + 1] = (Math.random() - 0.5) * 0.3;
      velocities[i * 3 + 2] = -(Math.random() * 2 + 1); // spray forward (negative Z)
      lifetimes[i] = Math.random();
    }
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.userData = { velocities, lifetimes };

    const material = new THREE.PointsMaterial({
      color: 0xddeeff,
      size: 0.015,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const particles = new THREE.Points(geometry, material);
    particles.visible = false;
    nozzleNode.add(particles);
    sprayRef.current = particles;

    // Expose spray toggle
    window._toggleSpray = (on) => { sprayActiveRef.current = on; particles.visible = on; };

    return () => {
      nozzleNode.remove(particles);
      geometry.dispose();
      material.dispose();
    };
  }, [scene, url]);

  // Create fire particle systems at FireOrigin nodes
  const fireZonesRef = useRef([]);
  useEffect(() => {
    if (!scene) return;
    
    const fireOrigins = [];
    scene.traverse(child => {
      if (child.name.startsWith('FireOrigin')) fireOrigins.push(child);
    });
    
    if (fireOrigins.length === 0) return;
    console.log(`[Model] Found ${fireOrigins.length} FireOrigin nodes — creating fire`);

    const zones = fireOrigins.map((origin, i) => {
      const count = 120;
      const geo = new THREE.BufferGeometry();
      const positions = new Float32Array(count * 3);
      const velocities = new Float32Array(count * 3);
      const lifetimes = new Float32Array(count);

      for (let j = 0; j < count; j++) {
        positions[j * 3] = (Math.random() - 0.5) * 0.15;
        positions[j * 3 + 1] = Math.random() * 0.3;
        positions[j * 3 + 2] = (Math.random() - 0.5) * 0.15;
        velocities[j * 3] = (Math.random() - 0.5) * 0.2;
        velocities[j * 3 + 1] = Math.random() * 1.5 + 0.5; // flames rise
        velocities[j * 3 + 2] = (Math.random() - 0.5) * 0.2;
        lifetimes[j] = Math.random();
      }
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geo.userData = { velocities, lifetimes };

      const mat = new THREE.PointsMaterial({
        color: 0xff6600,
        size: 0.04,
        transparent: true,
        opacity: 0.8,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });

      const particles = new THREE.Points(geo, mat);
      origin.add(particles);

      // Point light for glow
      const light = new THREE.PointLight(0xff4400, 2, 2);
      light.position.set(0, 0.15, 0);
      origin.add(light);

      return { particles, light, geo, mat, hp: 3.0, origin, active: true };
    });

    fireZonesRef.current = zones;
    window._fireZones = zones;
    window._fireActive = true;

    return () => {
      zones.forEach(z => {
        z.origin.remove(z.particles);
        z.origin.remove(z.light);
        z.geo.dispose();
        z.mat.dispose();
      });
    };
  }, [scene, url]);

  // Animate mixer + spray + fire particles each frame
  useFrame((_, delta) => {
    if (mixerRef.current) mixerRef.current.update(delta);

    if (sprayActiveRef.current && sprayRef.current) {
      const geo = sprayRef.current.geometry;
      const pos = geo.attributes.position.array;
      const { velocities, lifetimes } = geo.userData;
      const count = pos.length / 3;
      for (let i = 0; i < count; i++) {
        lifetimes[i] -= delta * 2;
        if (lifetimes[i] <= 0) {
          // Reset particle to origin
          pos[i * 3] = 0;
          pos[i * 3 + 1] = 0;
          pos[i * 3 + 2] = 0;
          lifetimes[i] = Math.random() * 0.8 + 0.2;
          velocities[i * 3] = (Math.random() - 0.5) * 0.3;
          velocities[i * 3 + 1] = (Math.random() - 0.5) * 0.3;
          velocities[i * 3 + 2] = -(Math.random() * 2 + 1);
        } else {
          pos[i * 3] += velocities[i * 3] * delta;
          pos[i * 3 + 1] += velocities[i * 3 + 1] * delta;
          pos[i * 3 + 2] += velocities[i * 3 + 2] * delta;
        }
      }
      geo.attributes.position.needsUpdate = true;
    }

    // Animate fire particles
    const zones = fireZonesRef.current;
    for (let z = 0; z < zones.length; z++) {
      const zone = zones[z];
      if (!zone.active) continue;
      
      const fgeo = zone.geo;
      const fpos = fgeo.attributes.position.array;
      const fvel = fgeo.userData.velocities;
      const flife = fgeo.userData.lifetimes;
      const fcount = fpos.length / 3;
      
      for (let i = 0; i < fcount; i++) {
        flife[i] -= delta * 1.5;
        if (flife[i] <= 0) {
          fpos[i * 3] = (Math.random() - 0.5) * 0.15;
          fpos[i * 3 + 1] = 0;
          fpos[i * 3 + 2] = (Math.random() - 0.5) * 0.15;
          flife[i] = Math.random() * 0.8 + 0.2;
        } else {
          fpos[i * 3] += fvel[i * 3] * delta;
          fpos[i * 3 + 1] += fvel[i * 3 + 1] * delta;
          fpos[i * 3 + 2] += fvel[i * 3 + 2] * delta;
        }
      }
      fgeo.attributes.position.needsUpdate = true;
      
      // Flicker the light
      zone.light.intensity = 1.5 + Math.sin(Date.now() * 0.01 + z) * 0.5;
      
      // Extinguish if spray is active — reduce HP based on proximity
      if (sprayActiveRef.current && zone.hp > 0) {
        zone.hp -= delta * 0.8;
        zone.mat.opacity = Math.max(0.1, zone.hp / 3.0);
        zone.light.intensity *= (zone.hp / 3.0);
        
        if (zone.hp <= 0) {
          zone.active = false;
          zone.particles.visible = false;
          zone.light.intensity = 0;
          console.log(`[Fire] Zone ${z} extinguished!`);
          
          // Check if all zones out
          const allOut = zones.every(zz => !zz.active);
          if (allOut) {
            window._fireActive = false;
            console.log('[Fire] ALL FIRE EXTINGUISHED!');
          }
        }
      }
    }
  });

  // Auto-scale: match Unity WorkshopModelLoader logic
  // GLB models come in wildly different units (cm, inches, mm, meters).
  // Compute bounding box and scale so largest dimension fits TARGET_SIZE.
  // Then center X/Z on origin and place model bottom at Y=0.
  useEffect(() => {
    if (!gltf.scene || !innerRef.current) return;

    const TARGET_SIZE = 1.5; // meters — same as Unity loader

    // Reset transforms before measuring
    innerRef.current.scale.setScalar(1);
    innerRef.current.position.set(0, 0, 0);

    // Compute raw bounds of the loaded scene
    const box = new THREE.Box3().setFromObject(gltf.scene);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);

    console.log(`[Model] Raw bounds: size=(${size.x.toFixed(2)}, ${size.y.toFixed(2)}, ${size.z.toFixed(2)}), maxDim=${maxDim.toFixed(2)}m`);

    if (maxDim < 0.001) {
      console.warn('[Model] Model has near-zero bounds — skipping auto-scale');
      return;
    }

    let scaleFactor = 1;
    if (maxDim > TARGET_SIZE * 1.5) {
      // Model is significantly oversized — scale down
      scaleFactor = TARGET_SIZE / maxDim;
      console.log(`[Model] Auto-scaled DOWN: ${maxDim.toFixed(1)}m → scale=${scaleFactor.toFixed(4)} (target ${TARGET_SIZE}m)`);
    } else if (maxDim < 0.05) {
      // Model is tiny (probably millimeters) — scale up
      scaleFactor = TARGET_SIZE / maxDim;
      console.log(`[Model] Auto-scaled UP: ${maxDim.toFixed(4)}m → scale=${scaleFactor.toFixed(2)} (target ${TARGET_SIZE}m)`);
    } else {
      console.log(`[Model] Size OK: ${maxDim.toFixed(2)}m (no auto-scale needed)`);
    }

    // Apply scale
    innerRef.current.scale.setScalar(scaleFactor);

    // Position: center X/Z on origin, bottom of model at Y=0
    // After scaling by scaleFactor, bounds become bounds * scaleFactor
    innerRef.current.position.set(
      -center.x * scaleFactor,
      -box.min.y * scaleFactor,
      -center.z * scaleFactor
    );

    console.log(`[Model] Repositioned: offset=(${(-center.x * scaleFactor).toFixed(3)}, ${(-box.min.y * scaleFactor).toFixed(3)}, ${(-center.z * scaleFactor).toFixed(3)})`);
  }, [scene, url]);

  const off = offset || [0, 0, 0];
  const rd = rotDelta || [0, 0, 0, 1];
  // rotDelta is a quaternion delta from spawn; apply it to the base Y rotation
  const baseQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, yRotation || 0, 0));
  const deltaQuat = new THREE.Quaternion(rd[0], rd[1], rd[2], rd[3]);
  const finalQuat = deltaQuat.multiply(baseQuat);
  const finalEuler = new THREE.Euler().setFromQuaternion(finalQuat);
  const s = modelScale || 1;
  return (
    <group ref={outerRef} position={[off[0], off[1], off[2]]} rotation={finalEuler} scale={[s, s, s]}>
      <group ref={innerRef}>
        <primitive object={scene} />
      </group>
    </group>
  );
}

// Error boundary for model loading failures
function ModelWithFallback({ url, yRotation, offset, rotDelta, modelScale, outerRef }) {
  const [hasError, setHasError] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState('');

  React.useEffect(() => {
    setHasError(false);
    setErrorMsg('');
  }, [url]);

  if (hasError) {
    return (
      <mesh position={[0, 1, 0]}>
        <boxGeometry args={[0.5, 0.5, 0.5]} />
        <meshStandardMaterial color="#ef4444" wireframe />
      </mesh>
    );
  }

  return (
    <React.Suspense fallback={
      <mesh position={[0, 0.5, 0]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="#6264A7" wireframe />
      </mesh>
    }>
      <ErrorCatcher onError={(e) => { setHasError(true); setErrorMsg(e); }}>
        <Model url={url} yRotation={yRotation} offset={offset} rotDelta={rotDelta} modelScale={modelScale} outerRef={outerRef} />
      </ErrorCatcher>
    </React.Suspense>
  );
}

// Simple error boundary as a function-friendly wrapper
class ErrorCatcher extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error) {
    console.error('[ModelLoad] Error:', error.message);
    this.props.onError?.(error.message);
  }
  render() {
    return this.state.hasError ? null : this.props.children;
  }
}


// =========================================================================
// Camera Pose Broadcaster — WS-034
// Sends desktop presenter's camera as pose messages at 10Hz so
// followers (headsets + browsers) lock to the desktop camera.
// =========================================================================
function CameraPoseBroadcaster({ active }) {
  const { camera } = useThree();
  const lastSendRef = useRef(0);
  const seqRef = useRef(0);

  useFrame(() => {
    if (!active || !window._workshopWs || window._workshopWs.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (now - lastSendRef.current < 100) return; // 10Hz
    lastSendRef.current = now;
    seqRef.current++;

    const p = camera.position;
    const q = camera.quaternion;
    // Reverse 3JS→Unity conversion: pos negate Z, quat negate X and Y
    const msg = {
      type: 'pose',
      seq: seqRef.current,
      cam: {
        p: [p.x, p.y, -p.z],
        r: [-q.x, -q.y, q.z, q.w],
      },
      hands: { l: null, r: null },
      gaze: null,
    };
    window._workshopWs.send(JSON.stringify(msg));
  });

  return null;
}


// =========================================================================
// Desktop Model Manipulator — WS-033
// TransformControls gizmo for mouse-based model grab/rotate.
// Sends model_offset at 10Hz while user is dragging.
// =========================================================================
function DesktopModelManipulator({ groupRef, transformMode, active, onOffsetChange }) {
  const lastSendRef = useRef(0);
  const draggingRef = useRef(false);

  // Send model_offset from the group's current transform
  const broadcastOffset = useCallback(() => {
    if (!groupRef.current || !window._workshopWs || window._workshopWs.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (now - lastSendRef.current < 100) return; // 10Hz
    lastSendRef.current = now;

    const p = groupRef.current.position;
    const q = groupRef.current.quaternion;
    // Reverse 3JS→Unity: pos negate Z, quat negate X and Y
    const msg = {
      type: 'model_offset',
      pos: [p.x, p.y, -p.z],
      rot: [-q.x, -q.y, q.z, q.w],
    };
    window._workshopWs.send(JSON.stringify(msg));
    // Also update local state so DrawingRenderer stays in sync
    onOffsetChange?.(p.x, p.y, p.z, q.x, q.y, q.z, q.w);
  }, [groupRef, onOffsetChange]);

  if (!active || !groupRef.current) return null;

  return (
    <TransformControls
      object={groupRef.current}
      mode={transformMode || 'translate'}
      onMouseDown={() => { draggingRef.current = true; }}
      onMouseUp={() => { draggingRef.current = false; broadcastOffset(); }}
      onChange={() => { if (draggingRef.current) broadcastOffset(); }}
    />
  );
}


// =========================================================================
// Desktop Presenter Toolbar — WS-031 + WS-032
// Overlay UI for model picker, transform mode, claim presenter.
// =========================================================================
function DesktopPresenterToolbar({ 
  isPresenter, tenant, roomCode, signalingBase, desktopUserId,
  transformMode, setTransformMode, onClaimPresenter
}) {
  const [models, setModels] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);

  // Fetch model manifest
  const fetchModels = useCallback(async () => {
    if (!tenant) return;
    setLoadingModels(true);
    try {
      const res = await fetch(`${signalingBase.http}/api/models?tenant=${tenant}`);
      if (res.ok) {
        const data = await res.json();
        setModels(data.models || []);
      }
    } catch (e) {
      console.error('[ModelPicker] fetch error:', e);
    }
    setLoadingModels(false);
  }, [tenant, signalingBase]);

  useEffect(() => { if (tenant) fetchModels(); }, [tenant, fetchModels]);

  const handleModelSelect = (model) => {
    const url = model.url || `https://humulo-models.s3.amazonaws.com/tenants/${model.source || tenant}/${model.fileName}`;
    if (window._workshopWs && window._workshopWs.readyState === WebSocket.OPEN) {
      window._workshopWs.send(JSON.stringify({
        type: 'model_swap',
        modelUrl: url,
        modelName: model.name,
        modelId: model.id,
      }));
      console.log(`[DesktopPresenter] model_swap sent: ${model.name}`);
    }
    setShowPicker(false);
  };

  return (
    <div style={{
      position: 'absolute', top: 8, left: 8,
      display: 'flex', flexDirection: 'column', gap: 6,
      fontFamily: 'system-ui', zIndex: 9998, maxWidth: 320,
    }}>
      {/* Presenter badge */}
      <div style={{
        padding: '6px 12px', background: 'rgba(17,24,39,0.95)', borderRadius: 8,
        border: `1px solid ${isPresenter ? '#6264A7' : '#334155'}`,
        color: isPresenter ? '#a5b4fc' : '#94a3b8', fontSize: 12, fontWeight: 600,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ fontSize: 14 }}>🖥</span>
        <span>Desktop Presenter</span>
        {isPresenter && <span style={{ color: '#22c55e', fontSize: 10 }}>● LIVE</span>}
      </div>

      {/* Claim Presenter (if not already) */}
      {!isPresenter && (
        <button onClick={onClaimPresenter} style={{
          padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
          background: '#6264A7', color: '#fff', fontSize: 13, fontWeight: 600,
        }}>
          🎤 Take Control
        </button>
      )}

      {/* Transform mode toggles */}
      {isPresenter && (
        <div style={{ display: 'flex', gap: 4 }}>
          {['translate', 'rotate', 'scale'].map(mode => (
            <button key={mode} onClick={() => setTransformMode(mode)} style={{
              flex: 1, padding: '6px 8px', borderRadius: 6, border: 'none', cursor: 'pointer',
              background: transformMode === mode ? '#6264A7' : '#1e293b',
              color: '#e2e8f0', fontSize: 11, fontWeight: 500, textTransform: 'capitalize',
            }}>
              {mode === 'translate' ? '↔ Move' : mode === 'rotate' ? '🔄 Rotate' : '⤡ Scale'}
            </button>
          ))}
        </div>
      )}

      {/* Model Picker toggle */}
      {isPresenter && (
        <button onClick={() => setShowPicker(!showPicker)} style={{
          padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
          background: showPicker ? '#334155' : '#1e293b', color: '#e2e8f0',
          fontSize: 13, fontWeight: 500, textAlign: 'left',
          border: '1px solid #334155',
        }}>
          📦 {showPicker ? 'Hide Models' : 'Change Model'}
        </button>
      )}

      {/* Model list */}
      {showPicker && isPresenter && (
        <div style={{
          background: 'rgba(17,24,39,0.98)', borderRadius: 8, border: '1px solid #334155',
          maxHeight: 400, overflowY: 'auto', padding: 8,
        }}>
          {loadingModels ? (
            <div style={{ color: '#64748b', fontSize: 12, padding: 8 }}>Loading models...</div>
          ) : models.length === 0 ? (
            <div style={{ color: '#64748b', fontSize: 12, padding: 8 }}>No models found for tenant "{tenant}"</div>
          ) : models.map(model => (
            <div key={model.id} onClick={() => handleModelSelect(model)} style={{
              padding: '10px 12px', borderRadius: 6, cursor: 'pointer',
              marginBottom: 4, transition: 'background 0.15s',
              background: '#0f172a', border: '1px solid #1e293b',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#1e293b'}
            onMouseLeave={e => e.currentTarget.style.background = '#0f172a'}
            >
              <div style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600 }}>{model.name}</div>
              {model.description && <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>{model.description}</div>}
              {model.source === 'humulo-library' && (
                <span style={{ color: '#6264A7', fontSize: 10, marginTop: 2, display: 'inline-block' }}>📚 Library</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// =========================================================================
// Diagnostic Overlay — shows all pose values + controls
// =========================================================================
function DiagPanel({ modelYRot, setModelYRot, calibrationYRot }) {
  const [diag, setDiag] = React.useState(null);
  const [collapsed, setCollapsed] = React.useState(true);  // Start collapsed in production
  
  React.useEffect(() => {
    const iv = setInterval(() => {
      if (window._diagData) setDiag({...window._diagData});
    }, 300);
    return () => clearInterval(iv);
  }, []);

  const calibDeg = ((calibrationYRot || 0) * 180 / Math.PI).toFixed(0);
  const modelYDeg = ((modelYRot || 0) * 180 / Math.PI).toFixed(0);

  if (collapsed) {
    return (
      <div onClick={() => setCollapsed(false)} style={{
        position: 'absolute', top: 8, right: 8, padding: '4px 10px',
        background: 'rgba(0,0,0,0.8)', borderRadius: 6, cursor: 'pointer',
        fontFamily: 'monospace', fontSize: 11, color: '#fbbf24', zIndex: 9999,
        border: '1px solid #f59e0b',
      }}>
        DIAG ▼ {diag ? `Y=${diag.yaw}° M=${modelYDeg}° cal=${calibDeg}°` : '...'}
      </div>
    );
  }

  return (
    <div style={{
      position: 'absolute', top: 8, left: 8, right: 8, maxWidth: 500,
      padding: '10px 14px',
      background: 'rgba(0,0,0,0.92)', borderRadius: 8,
      border: '2px solid #f59e0b',
      fontFamily: 'monospace', fontSize: 12, color: '#e2e8f0',
      zIndex: 9999,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ color: '#fbbf24', fontWeight: 700, fontSize: 13 }}>POSE DIAGNOSTICS</span>
        <span onClick={() => setCollapsed(true)} style={{ cursor: 'pointer', color: '#64748b' }}>▲ collapse</span>
      </div>
      
      {diag ? (
        <>
          <div style={{ color: '#94a3b8', marginBottom: 4 }}>
            <span style={{ color: '#f59e0b' }}>Unity Quat:</span> [{diag.unity_quat.join(', ')}]
          </div>
          <div style={{ color: '#94a3b8', marginBottom: 4 }}>
            <span style={{ color: '#f59e0b' }}>Unity Pos:</span> [{diag.unity_pos.join(', ')}]
          </div>
          <div style={{ marginBottom: 4 }}>
            <span style={{ color: '#10b981', fontWeight: 700, fontSize: 16 }}>
              YAW={diag.yaw}°
            </span>
            {' '}
            <span style={{ color: '#60a5fa' }}>PITCH={diag.pitch}°</span>
            {' '}
            <span style={{ color: '#a78bfa' }}>ROLL={diag.roll}°</span>
            {' '}
            <span style={{ color: '#64748b', fontSize: 10 }}>seq#{diag.seq}</span>
          </div>
        </>
      ) : (
        <div style={{ color: '#64748b' }}>Waiting for poses...</div>
      )}
      
      <hr style={{ border: 'none', borderTop: '1px solid #334155', margin: '8px 0' }} />
      
      {/* Model Y rotation slider — for manual override testing */}
      <div>
        <div style={{ color: '#f59e0b', fontSize: 11, marginBottom: 4 }}>
          MODEL Y ROTATION: <span style={{ color: '#10b981', fontWeight: 700 }}>{modelYDeg}°</span>
          {calibrationYRot ? <span style={{ color: '#64748b', marginLeft: 8 }}>(from calibration: {calibDeg}°)</span> : null}
        </div>
        <input type="range" min={-180} max={180} step={5}
          value={Math.round((modelYRot || 0) * 180 / Math.PI)}
          onChange={(e) => setModelYRot(parseFloat(e.target.value) * Math.PI / 180)}
          style={{ width: '100%', accentColor: '#f59e0b' }}
        />
        <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
          {[0, 90, -90, 180].map(deg => (
            <button key={deg} onClick={() => setModelYRot(deg * Math.PI / 180)}
              style={{
                padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer',
                fontSize: 10, fontFamily: 'monospace', background: '#1e293b', color: '#94a3b8',
              }}>
              {deg}°
            </button>
          ))}
        </div>
      </div>

      <hr style={{ border: 'none', borderTop: '1px solid #334155', margin: '8px 0' }} />
      <div style={{ fontSize: 10, color: '#64748b' }}>
        Tip: Look straight at model in VR → YAW should be ~0°. If off by ~90°, try different quat mode or model rotation.
      </div>
    </div>
  );
}


// =========================================================================
// Room Code Entry UI
// =========================================================================
function RoomCodeEntry({ onJoinRoom, loading, error }) {
  const [code, setCode] = useState('');
  
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#0a0e17', fontFamily: 'system-ui', color: '#e2e8f0',
    }}>
      <div style={{
        background: 'rgba(17, 24, 39, 0.95)', borderRadius: 12, padding: '40px 48px',
        border: '1px solid #1e293b', textAlign: 'center',
      }}>
        <div style={{ color: '#6264A7', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
          Humulo Workshop
        </div>
        <div style={{ color: '#64748b', fontSize: 14, marginBottom: 24 }}>
          Enter the 5-digit code shown on the presenter's headset
        </div>
        <input
          type="text" maxLength={5} value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="00000"
          style={{
            width: 200, padding: '12px 16px', fontSize: 32, textAlign: 'center',
            letterSpacing: 8, fontWeight: 700, fontFamily: 'monospace',
            background: '#1e293b', border: '2px solid #334155', borderRadius: 8,
            color: '#e2e8f0', outline: 'none',
          }}
          onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 5) onJoinRoom(code); }}
          autoFocus
        />
        <br />
        <button
          onClick={() => onJoinRoom(code)}
          disabled={code.length !== 5 || loading}
          style={{
            marginTop: 16, padding: '10px 32px', borderRadius: 8, border: 'none',
            background: code.length === 5 ? '#6264A7' : '#334155',
            color: '#e2e8f0', fontSize: 14, fontWeight: 600,
            cursor: code.length === 5 ? 'pointer' : 'default',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Connecting...' : 'Join Workshop'}
        </button>
        {error && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 12 }}>{error}</div>}
      </div>
    </div>
  );
}


// =========================================================================
// Interactive Model Controls — trigger animations and spray for interactive GLBs
// =========================================================================
function InteractionPanel() {
  const [hasAnimations, setHasAnimations] = React.useState(false);
  const [pinPulled, setPinPulled] = React.useState(false);
  const [spraying, setSpraying] = React.useState(false);
  const [hasFire, setHasFire] = React.useState(false);
  const [fireOut, setFireOut] = React.useState(false);

  React.useEffect(() => {
    const iv = setInterval(() => {
      if (window._modelAnimations && Object.keys(window._modelAnimations).length > 0) {
        setHasAnimations(true);
      }
      if (window._fireZones && window._fireZones.length > 0) {
        setHasFire(true);
        const allOut = window._fireZones.every(z => !z.active);
        if (allOut && !fireOut) setFireOut(true);
        if (!allOut && fireOut) setFireOut(false); // reset came through
      }
    }, 300);
    return () => clearInterval(iv);
  }, []);

  // Keyboard shortcuts: P = pull pin, S = toggle spray
  React.useEffect(() => {
    const handler = (e) => {
      if (e.key === 'p' || e.key === 'P') {
        if (!pinPulled && window._modelAnimations?.PinPull) {
          window._modelAnimations.PinPull.reset().play();
          setPinPulled(true);
        }
      }
      if (e.key === 's' || e.key === 'S') {
        if (pinPulled) {
          const next = !spraying;
          setSpraying(next);
          window._toggleSpray?.(next);
          if (next && window._modelAnimations?.HandleSqueeze) {
            const a = window._modelAnimations.HandleSqueeze;
            a.setLoop(THREE.LoopRepeat);
            a.reset().play();
          } else if (window._modelAnimations?.HandleSqueeze) {
            window._modelAnimations.HandleSqueeze.stop();
          }
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [pinPulled, spraying]);

  if (!hasAnimations) return null;

  return (
    <div style={{
      position: 'absolute', top: 8, left: 8, 
      display: 'flex', flexDirection: 'column', gap: 6,
      fontFamily: 'system-ui', zIndex: 9990,
    }}>
      <div style={{
        padding: '6px 12px', background: 'rgba(17,24,39,0.95)', borderRadius: 8,
        border: '1px solid #334155', color: '#fbbf24', fontSize: 11, fontWeight: 600,
      }}>
        🔥 Interactive Model
      </div>
      <button
        onClick={() => {
          if (!pinPulled && window._modelAnimations?.PinPull) {
            window._modelAnimations.PinPull.reset().play();
            setPinPulled(true);
          }
        }}
        disabled={pinPulled}
        style={{
          padding: '8px 16px', borderRadius: 8, border: 'none', cursor: pinPulled ? 'default' : 'pointer',
          background: pinPulled ? '#064e3b' : '#dc2626', color: '#fff',
          fontSize: 13, fontWeight: 600, opacity: pinPulled ? 0.7 : 1,
        }}
      >
        {pinPulled ? '✅ Pin Pulled' : '📌 Pull Pin (P)'}
      </button>
      <button
        onClick={() => {
          if (!pinPulled) return;
          const next = !spraying;
          setSpraying(next);
          window._toggleSpray?.(next);
          if (next && window._modelAnimations?.HandleSqueeze) {
            const a = window._modelAnimations.HandleSqueeze;
            a.setLoop(THREE.LoopRepeat);
            a.reset().play();
          } else if (window._modelAnimations?.HandleSqueeze) {
            window._modelAnimations.HandleSqueeze.stop();
          }
        }}
        disabled={!pinPulled}
        style={{
          padding: '8px 16px', borderRadius: 8, border: 'none',
          cursor: pinPulled ? 'pointer' : 'default',
          background: spraying ? '#059669' : (pinPulled ? '#2563eb' : '#334155'),
          color: '#fff', fontSize: 13, fontWeight: 600,
          opacity: pinPulled ? 1 : 0.4,
        }}
      >
        {spraying ? '🧯 Spraying... (S)' : '💨 Spray (S)'}
      </button>
      {hasFire && (
        <div style={{
          padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
          background: fireOut ? 'rgba(5,150,105,0.9)' : 'rgba(220,38,38,0.9)',
          color: '#fff', textAlign: 'center',
        }}>
          {fireOut ? '✅ Fire Extinguished!' : '🔥 Fire Active — Put it out!'}
        </div>
      )}
    </div>
  );
}

// =========================================================================
// Main Viewer Component
// =========================================================================
export default function ThreeViewer({ context, isTeams, meetingJoinUrl, ssoToken }) {
  const [roomCode, setRoomCode] = useState(null);
  const [modelUrl, setModelUrl] = useState(null);
  const [modelOffset, setModelOffset] = useState([0, 0, 0]); // delta from spawn position
  const [modelRotDelta, setModelRotDelta] = useState([0, 0, 0, 1]); // quaternion delta from spawn rotation
  const [followPresenter, setFollowPresenter] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [latestPose, setLatestPose] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modelYRot, setModelYRot] = useState(0);
  const [presenterUserId, setPresenterUserId] = useState(null);
  const [calibrationYRot, setCalibrationYRot] = useState(0);
  const [showQR, setShowQR] = useState(false);
  const [props, setProps] = useState({}); // { propId: { modelUrl, position, rotation, held } }
  const [participantPoses, setParticipantPoses] = useState({}); // WS-018: all VR user poses
  const [modelScale, setModelScale] = useState(1); // WS-017: presenter-controlled scale
  const [laserData, setLaserData] = useState(null); // WS-019: { origin, direction, active, hitDistance }
  const [drawingStrokes, setDrawingStrokes] = useState([]); // WS-021: [{ id, points, color }]
  
  // WS-031: Desktop presenter mode
  const [isDesktopPresenter, setIsDesktopPresenter] = useState(false); // URL ?mode=presenter
  const [desktopIsLivePresenter, setDesktopIsLivePresenter] = useState(false); // actually claimed
  const [transformMode, setTransformMode] = useState('translate'); // translate|rotate|scale
  const modelGroupRef = useRef(null); // ref for TransformControls attachment
  const desktopUserIdRef = useRef('desktop_' + Math.random().toString(36).substr(2, 8));
  
  // Parse URL params for desktop presenter mode + tenant
  const urlParams = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      mode: params.get('mode'),
      tenant: params.get('tenant') || 'humulo-com',
      room: params.get('room'),
    };
  }, []);
  
  const signalingBase = useMemo(() => {
    const origin = window.location.origin;
    const wsOrigin = origin.replace('https://', 'wss://').replace('http://', 'ws://');
    return { http: origin, ws: wsOrigin };
  }, []);

  // Restore session on mount — handles Teams tab switching away (People panel, etc.)
  // sessionStorage survives tab focus changes but clears when the meeting ends.
  useEffect(() => {
    const saved = sessionStorage.getItem('workshop_room');
    if (saved) {
      try {
        const { code, modelUrl: savedUrl, modelYRot: savedRot } = JSON.parse(saved);
        if (code && /^\d{5}$/.test(code)) {
          console.log(`[Session] Restoring room ${code} from sessionStorage`);
          // Re-join silently: skip bind-teams (already bound), skip Teams audio re-trigger
          handleJoinRoom(code, { skipTeamsBind: true, restoreModelUrl: savedUrl });
          return; // don't also check URL param
        }
      } catch (e) { sessionStorage.removeItem('workshop_room'); }
    }

    // Auto-join from URL param: ?room=12345 (used when scanning QR code)
    // Desktop presenter: ?room=12345&mode=presenter&tenant=humulo-com
    const params = new URLSearchParams(window.location.search);
    const codeFromUrl = params.get('room');
    const modeFromUrl = params.get('mode');
    if (modeFromUrl === 'presenter') {
      setIsDesktopPresenter(true);
      setFollowPresenter(false); // Desktop presenter uses free camera
    }
    if (codeFromUrl && /^\d{5}$/.test(codeFromUrl)) {
      console.log(`[AutoJoin] Room code from URL: ${codeFromUrl} — mode=${modeFromUrl || 'viewer'}`);
      handleJoinRoom(codeFromUrl, { skipTeamsBind: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentional: only run on mount

  const handleJoinRoom = useCallback(async (code, opts = {}) => {
    const skipTeamsBind = opts.skipTeamsBind === true;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${signalingBase.http}/api/rooms/${code}`);
      if (!res.ok) {
        setError(res.status === 404 ? 'Room not found — check the code' : 'Server error');
        setLoading(false);
        return;
      }
      const room = await res.json();
      setRoomCode(code);

      // If restoring from sessionStorage, use saved model/rotation (room may have newer
      // model from a swap that happened while tab was backgrounded — but room.modelUrl
      // is the server-authoritative current model, so always use that).
      const finalModelUrl = room.modelUrl;
      setModelUrl(finalModelUrl);
      
      // Set presenter from server-authoritative room state
      if (room.presenterUserId) {
        setPresenterUserId(room.presenterUserId);
        console.log(`[Room] Presenter: ${room.presenterUserId}`);
      }
      // Restore model offset if presenter has moved the model
      if (room.lastModelOffset && room.lastModelOffset.pos) {
        const p = room.lastModelOffset.pos;
        const r = room.lastModelOffset.rot || [0,0,0,1];
        // Same conversion as real-time model_offset: pos negate Z, quat negate X and Y
        setModelOffset([p[0], p[1], -p[2]]);
        setModelRotDelta([-r[0], -r[1], r[2], r[3]]);
      } else {
        setModelOffset([0, 0, 0]);
        setModelRotDelta([0, 0, 0, 1]);
      }

      // WS-017: Restore model scale from room state
      setModelScale(room.modelScale ?? 1);
      // WS-021: Restore drawing strokes from room state
      setDrawingStrokes(room.drawingStrokes || []);

      // Model orientation: fixed Math.PI (180°) compensates for glTF -Z forward vs Unity +Z forward.
      // Poses are already EnvironmentMain-local (InverseTransformPoint), so calibrationYRot
      // does NOT need to be applied — the relative poses handle alignment automatically.
      const calYRot = room.calibrationYRot ?? 0;
      setCalibrationYRot(calYRot);
      setModelYRot(Math.PI);  // Fixed 180° — glTF front axis flip
      console.log(`[Room] Joined ${code}, calibrationYRot=${calYRot.toFixed(3)} rad (stored but not applied — poses are env-relative), modelYRot=π`);

      // Persist to sessionStorage so Teams tab switches don't lose the room
      try {
        sessionStorage.setItem('workshop_room', JSON.stringify({
          code, modelUrl: finalModelUrl
        }));
      } catch (e) { /* storage not available */ }

      setLoading(false);

      // --- ACS Audio Bridge: bind Teams meeting to this room ---
      // Only bind when the user deliberately enters a code (not auto-join from URL/QR).
      // Also skip if the room is already in teams voice mode (browser reconnect case) —
      // server is idempotent but this prevents redundant Graph API calls.
      const alreadyBound = room.voiceMode === 'teams';
      if (!skipTeamsBind && !alreadyBound && (meetingJoinUrl || context?.meeting?.id)) {
        try {
          const bindPayload = {
            meetingJoinUrl: meetingJoinUrl || null,
            meetingId: context?.meeting?.id || null,
            tenantId: context?.user?.tenant?.id || null,
            organizerName: context?.user?.displayName || 'Teams Host',
            ssoToken: ssoToken || null
          };
          console.log('[ACS] Binding Teams meeting to room', code, '— hasUrl:', !!meetingJoinUrl, 'hasSso:', !!ssoToken, 'hasMeetingId:', !!context?.meeting?.id);
          const bindRes = await fetch(`${signalingBase.http}/api/rooms/${code}/bind-teams`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(bindPayload)
          });
          if (bindRes.ok) {
            const bindData = await bindRes.json();
            console.log(`[ACS] ✅ Bound Teams meeting to room ${code}`, bindData.meetingJoinUrl ? '(URL resolved)' : '');
          } else {
            const errData = await bindRes.json().catch(() => ({}));
            console.warn(`[ACS] bind-teams failed:`, errData);
          }
        } catch (bindErr) {
          console.warn(`[ACS] bind-teams error:`, bindErr.message);
        }
      } else {
        console.log(`[ACS] No meetingJoinUrl or meetingId available — running standalone (not in Teams meeting)`);
      }
    } catch (err) {
      setError(`Connection failed: ${err.message}`);
      setLoading(false);
    }
  }, [signalingBase, meetingJoinUrl, ssoToken, context]);

  const handlePoseUpdate = useCallback((pose) => {
    setLatestPose(pose);
  }, []);

  const handleModelOffset = useCallback((x, y, z, rx = 0, ry = 0, rz = 0, rw = 1) => {
    setModelOffset([x, y, z]);
    setModelRotDelta([rx, ry, rz, rw]);
  }, []);

  // WS-033: Desktop manipulator local offset update (keeps drawing/UI in sync)
  const handleDesktopOffsetChange = useCallback((x, y, z, qx, qy, qz, qw) => {
    setModelOffset([x, y, z]);
    setModelRotDelta([qx, qy, qz, qw]);
  }, []);

  const handleInteractiveState = useCallback((data) => {
    // Presenter sent interactive state — update fire/spray/pin visuals
    if (data.pinPulled && window._modelAnimations?.PinPull) {
      window._modelAnimations.PinPull.reset().play();
    }
    if (data.spraying) {
      window._toggleSpray?.(true);
      if (window._modelAnimations?.HandleSqueeze) {
        const a = window._modelAnimations.HandleSqueeze;
        if (!a.isRunning()) { a.setLoop(THREE.LoopRepeat); a.reset().play(); }
      }
    } else {
      window._toggleSpray?.(false);
      if (window._modelAnimations?.HandleSqueeze) {
        window._modelAnimations.HandleSqueeze.stop();
      }
    }
    // Sync fire zone HP
    if (data.fireHP && window._fireZones) {
      data.fireHP.forEach((hp, i) => {
        if (i < window._fireZones.length) {
          const zone = window._fireZones[i];
          zone.hp = hp;
          const pct = Math.max(0, hp / 3.0);
          zone.mat.opacity = Math.max(0.1, pct);
          zone.light.intensity = 2 * pct;
          if (hp <= 0 && zone.active) {
            zone.active = false;
            zone.particles.visible = false;
            zone.light.intensity = 0;
          }
        }
      });
      if (data.fireExtinguished) window._fireActive = false;
    }
  }, []);

  const handlePropEvent = useCallback((data) => {
    if (data.type === 'prop_spawn') {
      console.log(`[Props] Spawning: ${data.propId} url=${data.modelUrl}`);
      setProps(prev => ({
        ...prev,
        [data.propId]: {
          modelUrl: data.modelUrl,
          position: data.p || [0, 1, 0],
          rotation: [0, 0, 0, 1],
          held: false,
        }
      }));
    } else if (data.type === 'object_transform') {
      setProps(prev => {
        const existing = prev[data.propId];
        if (!existing) return prev;
        return {
          ...prev,
          [data.propId]: {
            ...existing,
            position: data.p,
            rotation: data.r,
            held: !!data.held,
          }
        };
      });
    } else if (data.type === 'prop_despawn') {
      console.log(`[Props] Despawning: ${data.propId}`);
      setProps(prev => {
        const next = { ...prev };
        delete next[data.propId];
        return next;
      });
    }
  }, []);

  // WS-018: All VR participant poses update
  const handleParticipantsChange = useCallback((poses) => {
    setParticipantPoses(poses);
  }, []);

  // WS-017: Model scale from presenter
  const handleModelScale = useCallback((scale) => {
    console.log(`[Viewer] Model scale: ${scale}`);
    setModelScale(scale);
  }, []);

  // WS-019: Laser pointer from presenter
  const handleLaserPointer = useCallback((data) => {
    if (!data.active) { setLaserData({ active: false }); return; }
    // Unity sends origin "o" and hit point "h" — compute direction + distance
    if (data.o && data.h) {
      const ox = data.o[0], oy = data.o[1], oz = data.o[2];
      const hx = data.h[0], hy = data.h[1], hz = data.h[2];
      const dx = hx - ox, dy = hy - oy, dz = hz - oz;
      const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
      if (dist > 0.001) {
        setLaserData({ active: true, origin: data.o, direction: [dx/dist, dy/dist, dz/dist], hitDistance: dist });
      }
    } else {
      // Legacy format: origin + direction
      setLaserData(data);
    }
  }, []);

  // WS-021: Drawing strokes from presenter
  const handleDrawingStroke = useCallback((data) => {
    if (data.action === 'point' && data.id != null && data.p) {
      // Individual point from Unity — accumulate into stroke (world coords with Z-flip on render)
      console.log(`[Drawing] point id=${data.id} p=[${data.p.map(v=>v.toFixed(3))}]`);
      setDrawingStrokes(prev => {
        const existing = prev.find(s => s.id === data.id);
        if (existing) {
          return prev.map(s => s.id === data.id ? { ...s, points: [...s.points, data.p] } : s);
        } else {
          return [...prev, { id: data.id, points: [data.p], color: data.color || '#ff0000' }];
        }
      });
    } else if (data.action === 'start') {
      // Legacy: batch start
      setDrawingStrokes(prev => [...prev, { id: data.strokeId, points: data.points || [], color: data.color || '#ff0000' }]);
    } else if (data.action === 'update') {
      setDrawingStrokes(prev => prev.map(s => s.id === data.strokeId ? { ...s, points: data.points } : s));
    } else if (data.action === 'clear') {
      setDrawingStrokes([]);
    }
  }, []);

  // WS-026: Claim presenter from desktop
  const handleClaimPresenter = useCallback(async () => {
    if (!roomCode) return;
    try {
      const res = await fetch(`${signalingBase.http}/api/rooms/${roomCode}/claim-presenter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: desktopUserIdRef.current }),
      });
      if (res.ok) {
        setDesktopIsLivePresenter(true);
        setPresenterUserId(desktopUserIdRef.current);
        console.log(`[DesktopPresenter] Claimed presenter in room ${roomCode}`);
      } else {
        const err = await res.json().catch(() => ({}));
        console.error('[DesktopPresenter] Claim failed:', err);
      }
    } catch (e) {
      console.error('[DesktopPresenter] Claim error:', e);
    }
  }, [roomCode, signalingBase]);

  // Track presenter changes — update desktop presenter live status
  useEffect(() => {
    if (isDesktopPresenter) {
      setDesktopIsLivePresenter(presenterUserId === desktopUserIdRef.current);
    }
  }, [presenterUserId, isDesktopPresenter]);

  const handleLeaveRoom = useCallback(() => {
    console.log('[Session] Leaving room — clearing state');
    sessionStorage.removeItem('workshop_room');
    setRoomCode(null);
    setModelUrl(null);
    setModelYRot(0);
    setModelOffset([0, 0, 0]);
    setModelRotDelta([0, 0, 0, 1]);
    setModelScale(1);
    setParticipantPoses({});
    setLaserData(null);
    setDrawingStrokes([]);
    setConnectionStatus('disconnected');
    setLoading(false);
    setError(null);
  }, []);

  const handleModelSwap = useCallback((newModelUrl, modelName) => {
    console.log(`[Viewer] Model swap: ${modelName} -> ${newModelUrl}`);
    setModelUrl(newModelUrl);
    setModelOffset([0, 0, 0]);
    setModelRotDelta([0, 0, 0, 1]); // reset transforms on model change
    setModelScale(1); // WS-017: reset scale on model change
    setDrawingStrokes([]); // WS-021: clear drawings on model change
    // Keep sessionStorage in sync so tab restore gets the current model
    try {
      const saved = sessionStorage.getItem('workshop_room');
      if (saved) {
        const parsed = JSON.parse(saved);
        sessionStorage.setItem('workshop_room', JSON.stringify({ ...parsed, modelUrl: newModelUrl }));
      }
    } catch (e) { /* ignore */ }
  }, []);

  const wsUrl = useMemo(() => {
    if (!roomCode) return null;
    const role = isDesktopPresenter ? 'desktop_presenter' : 'browser';
    const userId = isDesktopPresenter ? desktopUserIdRef.current : '';
    const displayName = isDesktopPresenter ? encodeURIComponent('Desktop Presenter') : '';
    let url = `${signalingBase.ws}?room=${roomCode}&role=${role}`;
    if (userId) url += `&userId=${userId}&displayName=${displayName}`;
    return url;
  }, [roomCode, signalingBase, isDesktopPresenter]);

  if (!roomCode) {
    return <RoomCodeEntry onJoinRoom={handleJoinRoom} loading={loading} error={error} />;
  }

  return (
    <div style={{ width: '100%', height: '100vh', position: 'relative', background: '#0a0e17' }}>
      <Canvas
        camera={{ position: [0, 1.5, 3], fov: 60 }}
        style={{ width: '100%', height: '100%' }}
        gl={{ toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.2 }}
        shadows
      >
        <ambientLight intensity={0.4} />
        <directionalLight
          position={[5, 8, 5]}
          intensity={1.5}
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
          shadow-camera-far={50}
          shadow-camera-left={-10}
          shadow-camera-right={10}
          shadow-camera-top={10}
          shadow-camera-bottom={-10}
          shadow-bias={-0.0001}
        />
        <Environment preset="studio" />
        {/* Shadow-receiving ground plane */}
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]} receiveShadow>
          <planeGeometry args={[50, 50]} />
          <shadowMaterial opacity={0.3} />
        </mesh>
        
        <PoseSync
          wsUrl={wsUrl}
          followPresenter={followPresenter && !isDesktopPresenter}
          onConnectionChange={setConnectionStatus}
          onPoseUpdate={handlePoseUpdate}
          onModelSwap={handleModelSwap}
          onModelOffset={handleModelOffset}
          onInteractiveState={handleInteractiveState}
          onPropEvent={handlePropEvent}
          onModelScale={handleModelScale}
          onLaserPointer={handleLaserPointer}
          onDrawingStroke={handleDrawingStroke}
          onParticipantsChange={handleParticipantsChange}
          initialPresenter={presenterUserId}
        />

        <RemoteAvatars participantPoses={participantPoses} presenterUserId={presenterUserId} followPresenter={followPresenter && !isDesktopPresenter} />
        <LaserPointerRenderer laserData={laserData} />
        <DrawingRenderer strokes={drawingStrokes} />
        <PropRenderer props={props} />
        {/* GazeRay removed */}
        <OrbitControls enabled={isDesktopPresenter || !followPresenter} />
        <CameraPoseBroadcaster active={desktopIsLivePresenter} />
        <DesktopModelManipulator 
          groupRef={modelGroupRef} 
          transformMode={transformMode} 
          active={desktopIsLivePresenter && !!modelUrl}
          onOffsetChange={handleDesktopOffsetChange}
        />
        <gridHelper args={[10, 10, '#334155', '#1e293b']} />
        
        {modelUrl && (
          <ModelWithFallback url={modelUrl} yRotation={modelYRot} offset={modelOffset} rotDelta={modelRotDelta} modelScale={modelScale} outerRef={desktopIsLivePresenter ? modelGroupRef : undefined} />
        )}
      </Canvas>

      {/* QR Code overlay — tap Share to show, scan to join on phone */}
      {showQR && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 10000,
        }} onClick={() => setShowQR(false)}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: 24, textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: 'system-ui', fontWeight: 700, fontSize: 15, color: '#0a0e17', marginBottom: 4 }}>
              Humulo Workshop
            </div>
            <div style={{ fontFamily: 'system-ui', fontSize: 12, color: '#64748b', marginBottom: 16 }}>
              Scan to join Room <strong>{roomCode}</strong> on your phone
            </div>
            <QRCode
              value={`${window.location.origin}?room=${roomCode}`}
              size={200}
              bgColor="#ffffff"
              fgColor="#0a0e17"
              level="M"
            />
            <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#94a3b8', marginTop: 12 }}>
              {window.location.origin}?room={roomCode}
            </div>
            <button onClick={() => setShowQR(false)} style={{
              marginTop: 16, padding: '8px 24px', borderRadius: 8, border: 'none',
              background: '#6264A7', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>Close</button>
          </div>
        </div>
      )}

      {/* Room badge + leave button */}
      <div style={{
        position: 'absolute', bottom: 60, right: 12, padding: '8px 16px',
        background: 'rgba(17, 24, 39, 0.95)', borderRadius: 8,
        border: '1px solid #334155', fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: 14, color: '#94a3b8',
        display: 'flex', alignItems: 'center', gap: 12,
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        zIndex: 10000,
      }}>
        <span>Room <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 16 }}>{roomCode}</span></span>
        <span style={{ color: connectionStatus === 'connected' ? '#22c55e' : '#ef4444', fontSize: 10 }}>●</span>
        <button onClick={handleLeaveRoom} style={{
          background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6,
          padding: '6px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600,
          letterSpacing: '0.02em',
        }}>Leave Room</button>
      </div>

      {/* DIAGNOSTIC PANEL */}
      <DiagPanel 
        modelYRot={modelYRot} setModelYRot={setModelYRot}
        calibrationYRot={calibrationYRot}
      />

      {/* Interactive Model Controls — only shown when model has animations AND not desktop presenter */}
      {!isDesktopPresenter && <InteractionPanel />}

      {/* WS-031: Desktop Presenter Toolbar */}
      {isDesktopPresenter && (
        <DesktopPresenterToolbar
          isPresenter={desktopIsLivePresenter}
          tenant={urlParams.tenant}
          roomCode={roomCode}
          signalingBase={signalingBase}
          desktopUserId={desktopUserIdRef.current}
          transformMode={transformMode}
          setTransformMode={setTransformMode}
          onClaimPresenter={handleClaimPresenter}
        />
      )}

      {/* HUD — bottom */}
      <div style={{
        position: 'absolute', bottom: 8, left: 8, right: 8,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '8px 12px',
        background: 'rgba(17, 24, 39, 0.9)',
        borderRadius: 8, border: '1px solid #1e293b',
        fontFamily: 'system-ui', fontSize: 12, color: '#e2e8f0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#6264A7', fontWeight: 600 }}>Humulo Workshop</span>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: connectionStatus === 'live' ? '#10b981' 
              : connectionStatus === 'connected' ? '#f59e0b' : '#475569',
          }} />
          <span style={{ color: '#64748b', fontSize: 11 }}>
            {connectionStatus === 'live' ? 'Receiving poses' 
              : connectionStatus === 'connected' ? 'Waiting for presenter' 
              : 'Disconnected'}
          </span>
          {!modelUrl && connectionStatus !== 'disconnected' && (
            <span style={{ color: '#f59e0b', fontSize: 10, marginLeft: 8 }}>
              ⏳ No model loaded yet
            </span>
          )}
          {meetingJoinUrl && (
            <span style={{ color: '#6264A7', fontSize: 10, marginLeft: 8, padding: '2px 6px', background: 'rgba(98,100,167,0.2)', borderRadius: 4 }}>
              🎙 Teams Audio Bridge Active
            </span>
          )}
        </div>
        {!isDesktopPresenter && (
          <button
            onClick={() => setFollowPresenter(!followPresenter)}
            style={{
              padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
              background: followPresenter ? '#6264A7' : '#1e293b',
              color: '#e2e8f0', fontSize: 11, fontWeight: 500,
            }}
          >
            {followPresenter ? '👁 Following' : '🖱 Free Look'}
          </button>
        )}
        <button
          onClick={() => setShowQR(true)}
          style={{
            padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
            background: '#1e293b', color: '#e2e8f0', fontSize: 11, fontWeight: 500,
          }}
        >
          📱 Share
        </button>
      </div>
    </div>
  );
}
